import { useCallback, useEffect, useMemo, useState } from 'react';
import { mockTrails } from '@/data/mockData';
import { supabase } from '@/lib/supabase';
import type { LegacyQuoteCard, Trail } from '@/types';

interface UseTrailsOptions {
  userId?: string | null;
  demo?: boolean;
}

interface TrailItemRow {
  card_json: unknown;
  created_at: string;
}

interface TrailRow {
  id: string;
  name: string;
  created_at: string;
  trail_items: TrailItemRow[] | null;
}

function parseCard(value: unknown): LegacyQuoteCard | null {
  let card = value;

  if (typeof card === 'string') {
    try {
      card = JSON.parse(card) as unknown;
    } catch {
      return null;
    }
  }

  if (!card || typeof card !== 'object') return null;
  const candidate = card as Partial<LegacyQuoteCard>;

  if (
    typeof candidate.id !== 'string' ||
    typeof candidate.quote !== 'string' ||
    typeof candidate.source !== 'string' ||
    typeof candidate.documentTitle !== 'string' ||
    typeof candidate.date !== 'string' ||
    typeof candidate.canonicalLink !== 'string' ||
    !candidate.lane ||
    typeof candidate.laneLabel !== 'string'
  ) {
    return null;
  }

  return candidate as LegacyQuoteCard;
}

function mapTrailRow(row: TrailRow): Trail {
  const cards = [...(row.trail_items ?? [])]
    .sort(
      (left, right) => new Date(left.created_at).getTime() - new Date(right.created_at).getTime()
    )
    .map((item) => parseCard(item.card_json))
    .filter((card): card is LegacyQuoteCard => card !== null);

  return {
    id: row.id,
    name: row.name,
    cards,
    createdAt: new Date(row.created_at),
  };
}

export function useTrails({ userId = null, demo = false }: UseTrailsOptions = {}) {
  const [trails, setTrails] = useState<Trail[]>(() => (demo ? mockTrails : []));
  const [activeTrailId, setActiveTrailId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(() => Boolean(userId && supabase));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId || !supabase) return;

    let isCurrent = true;

    supabase
      .from('trails')
      .select('id, name, created_at, trail_items(card_json, created_at)')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .then(({ data, error: queryError }) => {
        if (!isCurrent) return;

        if (queryError) {
          setError(queryError.message);
          setTrails([]);
        } else {
          setTrails(((data ?? []) as unknown as TrailRow[]).map(mapTrailRow));
        }
        setIsLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [userId]);

  const activeTrail = useMemo(
    () => trails.find((trail) => trail.id === activeTrailId) ?? null,
    [activeTrailId, trails]
  );

  const setActiveTrail = useCallback((trail: Trail | null) => {
    setActiveTrailId(trail?.id ?? null);
  }, []);

  const createTrail = useCallback(
    async (name: string): Promise<Trail> => {
      setError(null);

      if (userId && supabase) {
        const { data, error: insertError } = await supabase
          .from('trails')
          .insert({ user_id: userId, name })
          .select('id, name, created_at')
          .single();

        if (insertError || !data) {
          const message = insertError?.message ?? 'Unable to create trail';
          setError(message);
          throw new Error(message);
        }

        const newTrail: Trail = {
          id: data.id,
          name: data.name,
          cards: [],
          createdAt: new Date(data.created_at),
        };
        setTrails((previous) => [newTrail, ...previous]);
        setActiveTrailId(newTrail.id);
        return newTrail;
      }

      const newTrail: Trail = {
        id: crypto.randomUUID(),
        name,
        cards: [],
        createdAt: new Date(),
      };
      setTrails((previous) => [newTrail, ...previous]);
      setActiveTrailId(newTrail.id);
      return newTrail;
    },
    [userId]
  );

  const addToTrail = useCallback(
    async (trailId: string, card: LegacyQuoteCard): Promise<void> => {
      const trail = trails.find((candidate) => candidate.id === trailId);
      if (trail?.cards.some((savedCard) => savedCard.id === card.id)) return;

      setError(null);

      if (userId && supabase) {
        const { error: insertError } = await supabase.from('trail_items').insert({
          trail_id: trailId,
          card_id: card.id,
          card_json: card,
        });

        if (insertError) {
          setError(insertError.message);
          throw new Error(insertError.message);
        }
      }

      setTrails((previous) =>
        previous.map((candidate) =>
          candidate.id === trailId ? { ...candidate, cards: [...candidate.cards, card] } : candidate
        )
      );
    },
    [trails, userId]
  );

  const removeFromTrail = useCallback(
    async (trailId: string, cardId: string): Promise<void> => {
      setError(null);

      if (userId && supabase) {
        const { error: deleteError } = await supabase
          .from('trail_items')
          .delete()
          .eq('trail_id', trailId)
          .eq('card_id', cardId);

        if (deleteError) {
          setError(deleteError.message);
          throw new Error(deleteError.message);
        }
      }

      setTrails((previous) =>
        previous.map((trail) =>
          trail.id === trailId
            ? { ...trail, cards: trail.cards.filter((card) => card.id !== cardId) }
            : trail
        )
      );
    },
    [userId]
  );

  const deleteTrail = useCallback(
    async (trailId: string): Promise<void> => {
      setError(null);

      if (userId && supabase) {
        const { error: deleteError } = await supabase.from('trails').delete().eq('id', trailId);

        if (deleteError) {
          setError(deleteError.message);
          throw new Error(deleteError.message);
        }
      }

      setTrails((previous) => previous.filter((trail) => trail.id !== trailId));
      setActiveTrailId((current) => (current === trailId ? null : current));
    },
    [userId]
  );

  return {
    trails,
    activeTrail,
    setActiveTrail,
    createTrail,
    addToTrail,
    removeFromTrail,
    deleteTrail,
    isLoading,
    error,
  };
}
